/**
 * SignatureCanvas - Shared component for capturing signatures
 * Used in: TestRecordModal, RetestModal, ReactiveCompletionModal
 * 
 * Handles:
 * - Canvas setup with proper coordinate scaling
 * - Mouse and touch event handling (with non-passive touch for mobile)
 * - Drawing state management
 * - Clear functionality
 * - Export to base64 PNG
 */

import React, { useRef, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Button } from '@shared/components/ui/button';
import { Label } from '@shared/components/ui/label';
import { Trash2 } from 'lucide-react';

const SignatureCanvas = forwardRef(({
  label = 'Signature Required *',
  hint = 'Sign above to certify you have completed the work',
  disabled = false,
  height = 200,
  className = '',
  onSignatureChange,
}, ref) => {
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const touchStartTimeRef = useRef(null);
  const touchStartPosRef = useRef(null);
  const [_isDrawing, setIsDrawing] = useState(false);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    // Get signature as base64 PNG data URL, or null if empty
    getSignatureData: () => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const hasContent = imageData.data.some((val, idx) => idx % 4 === 3 && val > 0);
      
      if (!hasContent) return null;
      return canvas.toDataURL('image/png');
    },
    // Check if canvas has any signature content
    hasSignature: () => {
      const canvas = canvasRef.current;
      if (!canvas) return false;
      
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      return imageData.data.some((val, idx) => idx % 4 === 3 && val > 0);
    },
    // Clear the canvas
    clear: () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        onSignatureChange?.(null);
      }
    },
    // Get the canvas element itself
    getCanvas: () => canvasRef.current,
    // Load an existing signature from base64 data URL
    loadSignature: (dataUrl) => {
      const canvas = canvasRef.current;
      if (!canvas || !dataUrl) return;
      
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        // Clear first
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // Draw the loaded signature scaled to fit canvas
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        onSignatureChange?.(dataUrl);
      };
      img.src = dataUrl;
    },
  }));

  // Initialize canvas when mounted
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const initCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    };

    // Small delay to ensure container is rendered
    const timeoutId = setTimeout(initCanvas, 100);
    return () => clearTimeout(timeoutId);
  }, []);

  // Get scaled coordinates for accurate drawing
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    let clientX, clientY;
    if (e.touches && e.touches[0]) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    if (disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    e.preventDefault();
    isDrawingRef.current = true;
    setIsDrawing(true);
    
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawingRef.current || disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    e.preventDefault();
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawingRef.current) {
      isDrawingRef.current = false;
      setIsDrawing(false);
      // Notify parent of signature change
      onSignatureChange?.(canvasRef.current?.toDataURL('image/png'));
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      onSignatureChange?.(null);
    }
  };

  // Add non-passive touch event listeners for mobile signature capture
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || disabled) return;

    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';

    const getScaledCoords = (touch) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY
      };
    };

    const handleTouchStart = (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      const { x, y } = getScaledCoords(touch);
      
      // Store touch start time and position
      touchStartTimeRef.current = Date.now();
      touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
      
      // Don't start drawing immediately - wait for touchmove
      isDrawingRef.current = false;
      ctx.beginPath();
      ctx.moveTo(x, y);
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      const { x, y } = getScaledCoords(touch);
      
      // Check if this is a scroll gesture (started less than 150ms ago and moved significantly)
      if (touchStartTimeRef.current && touchStartPosRef.current) {
        const timeDiff = Date.now() - touchStartTimeRef.current;
        const xDiff = Math.abs(touch.clientX - touchStartPosRef.current.x);
        const yDiff = Math.abs(touch.clientY - touchStartPosRef.current.y);
        const distance = Math.sqrt(xDiff * xDiff + yDiff * yDiff);
        
        // Clear the refs after we've used them
        touchStartTimeRef.current = null;
        touchStartPosRef.current = null;
        
        // If touch started very recently and moved more than 10px, likely a scroll - ignore
        if (timeDiff < 150 && distance > 10) {
          return;
        }
        
        // Otherwise, start drawing if not already
        if (!isDrawingRef.current) {
          isDrawingRef.current = true;
          setIsDrawing(true);
        }
      }
      
      if (!isDrawingRef.current) return;
      
      ctx.lineTo(x, y);
      ctx.stroke();
    };

    const handleTouchEnd = (e) => {
      e.preventDefault();
      if (isDrawingRef.current) {
        isDrawingRef.current = false;
        setIsDrawing(false);
        onSignatureChange?.(canvas.toDataURL('image/png'));
      }
    };

    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [disabled, onSignatureChange]);

  return (
    <div className={`space-y-3 p-4 border-2 border-blue-300 rounded-lg bg-blue-50/50 ${className}`}>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{label}</Label>
        {!disabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={clearSignature}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Clear
          </Button>
        )}
      </div>
      <div className="border-2 border-dashed rounded-lg bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          className={`w-full cursor-crosshair touch-none ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ touchAction: 'none', minHeight: `${height}px`, height: `${height}px` }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
        />
      </div>
      {hint && (
        <p className="text-xs text-muted-foreground text-center">
          {hint}
        </p>
      )}
    </div>
  );
});

SignatureCanvas.displayName = 'SignatureCanvas';

export default SignatureCanvas;
